import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import noop from '../../../../utils/noop';
import { useStream } from '../../../../utils/hooks/useStream';
import { getCommentableTypeForDeviationType } from '../../../../DeviationPage/redux/helpers';
import Comment from '@wix/da-ds/pkg/Icons/24x24/Comment';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { getStreamId } from '../../../../CommentThread/helpers';
import ThreadStack from '../../../../CommentThread/_partials/ThreadStack';
import ThreadPostbox from '../../../../CommentThread/_partials/ThreadPostbox';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import s from './FeedCardCommentThread.scss';
import { BiData } from '@wix/da-bi/pkg/events';
const COMMENT_COUNT_TO_SHOW_BOTTOM_POST = 5;
const FeedCardCommentThread = (props)=>{
    const { deviation, className, isThreadOpen, withCommentSuggestions, openCommentThread = noop, showGenericNotification = noop } = props;
    const { t } = useTranslation();
    const commentableTypeId = getCommentableTypeForDeviationType(deviation);
    const commentableItemId = deviation.deviationId;
    const commentStreamId = getStreamId(commentableTypeId, commentableItemId);
    const streamData = useStream(commentStreamId);
    const { items, hasMore, isFetching, fetchNext: fetchMore } = streamData;
    const itemCount = items.length;
    // we have three sorts of comments to deal with here:
    // comments that are displayed
    // comments that the viewer published that are only saved locally
    // comments that are still on the server and haven't been fetched yet
    const openAndFetchMore = useCallback(()=>{
        !isThreadOpen && openCommentThread();
    }, [
        isThreadOpen,
        openCommentThread
    ]);
    const [numCommentsPosted, setNumberOfCommentsPosted] = useState(0);
    const onPublishSuccess = useCallback(()=>{
        setNumberOfCommentsPosted((posts)=>posts + 1);
        openAndFetchMore();
    }, [
        openAndFetchMore
    ]);
    const totalCommentsShown = itemCount + numCommentsPosted;
    useEffect(()=>{
        isThreadOpen && hasMore && fetchMore();
    }, [
        isThreadOpen,
        hasMore,
        fetchMore
    ]);
    if (!deviation.isCommentable) {
        return null;
    }
    const isBottomPostboxVisible = itemCount >= COMMENT_COUNT_TO_SHOW_BOTTOM_POST;
    // this can happen when we load the post in duperbrowse and return to the feed
    const weHaveCommentsNotDisplayed = !isThreadOpen && itemCount > 0;
    return /*#__PURE__*/ React.createElement("div", {
        className: className
    }, /*#__PURE__*/ React.createElement(ThreadPostbox, {
        isSmall: true,
        position: "topmost",
        className: s['commentbox'],
        commentableItemId: commentableItemId,
        commentableTypeId: commentableTypeId,
        withCommentSuggestions: withCommentSuggestions,
        onPublishSuccess: onPublishSuccess
    }), isThreadOpen && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(ThreadStack, {
        className: s['thread-stack'],
        commentableItemId: commentableItemId,
        commentableTypeId: commentableTypeId,
        maxCommentsShown: totalCommentsShown
    }), isFetching && /*#__PURE__*/ React.createElement(LoadingIndicator, {
        view: "llama"
    }), isBottomPostboxVisible && /*#__PURE__*/ React.createElement(ThreadPostbox, {
        isSmall: true,
        position: "bottom",
        className: s['commentbox'],
        commentableItemId: commentableItemId,
        commentableTypeId: commentableTypeId,
        withCommentSuggestions: withCommentSuggestions,
        onPublishSuccess: ()=>{
            // if we're in the lower box, we show a notification
            // so the user won't have to scroll up to see if it's posted
            showGenericNotification({
                message: t('comment.info.posted'),
                cardProps: {
                    iconComponent: Comment
                }
            });
            onPublishSuccess();
        }
    })), (hasMore || weHaveCommentsNotDisplayed) && /*#__PURE__*/ React.createElement(TextButton, {
        className: s['load-more'],
        variant: "main",
        size: "medium",
        onClick: isThreadOpen ? fetchMore : openAndFetchMore,
        disabled: isFetching,
        biData: BiData({
            evid: 182,
            itemid: commentableItemId,
            typeid: commentableTypeId
        }),
        noHorizontalPaddings: false
    }, t(isThreadOpen ? 'deviationview.feed.viewMoreComments' : 'deviationview.feed.viewComments')));
};
FeedCardCommentThread.displayName = 'FeedCardCommentThread';
export default FeedCardCommentThread;
